import { Localized } from "@fluent/react"
import { Badge, ButtonGroup, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TableCell, TableRow, TextField, Tooltip, Typography, Button, Stack } from "@mui/material"
import { memo, MutableRefObject, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react"
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { If } from "../../app/If"
import { NoteLabel } from "../ursOffer/NoteLabel"
import { ReqCriticalityLabel } from "./ReqCriticalityLabel"
import { CommentCounts } from "./requirementFilter"
import { restoreUrsRequirement, updateDeletedUrsRequirementReason, UrsGroup, URSRequirement, UrsRequirementType } from "./UrsSlice"
import { selectBlobsByResourceId } from "../documents/attachementsSlice"
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import CommentIcon from '@mui/icons-material/Comment'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import AttachmentIcon from '@mui/icons-material/Attachment'
import EditIcon from '@mui/icons-material/Edit'
import EditNoteIcon from '@mui/icons-material/EditNote'
import UndoIcon from '@mui/icons-material/Undo'
import { AppId } from "../../app/appTypes"
import { RequirementDescription } from "./RequirementDescription"
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { DocumentNumberBox } from "../../app/components/DocumentNumberBox"
import SmallStatusBadge from "../../app/components/SmallStatusBadge"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { showError, showSuccess } from "../notifications/notificationsSlice"

const DELETE_REASON_MAX_LENGTH = 1000

// Row actions that are unavailable while a reorder is pending. The rules live on the
// table container (RequirementsPanel), which toggles REORDER_ACTIVE — so entering and
// leaving reorder mode costs one class change instead of re-rendering every row.
// REORDER_HIDE collapses; REORDER_HIDE_SLOT keeps the space, for buttons whose column
// would otherwise jump.
export const REORDER_HIDE = 'urs-reorder-hide'
export const REORDER_HIDE_SLOT = 'urs-reorder-hide-slot'
export const REORDER_ACTIVE = 'urs-reordering'

export interface URSRequirementRowProps {
  id: AppId
  onAddChildRequirmentClick: (parentRequirement: URSRequirement) => void
  onEditClick: (id: AppId, group: UrsGroup | null, requirement: URSRequirement, rt: UrsRequirementType) => void
  onCopyClick: (id: AppId, group: UrsGroup | null, requirement: URSRequirement, rt: UrsRequirementType) => void
  onDeleteClick: (entity: URSRequirement) => void
  canEdit: boolean
  canAddChildRequirement: boolean
  canDelete: boolean
  showSupplierComment: boolean
  onCommentsClick: (requirement: URSRequirement) => void
  onAttachementsClick: (requirement: URSRequirement) => void
  template: boolean
  readOnly?: boolean
  requirement: URSRequirement
  group: UrsGroup & { typeDescription: string } | null
  rt: UrsRequirementType
  tableRef: MutableRefObject<null | HTMLDivElement>
  // Separate from canEdit: a filtered table still allows editing but must not allow
  // dragging, because drop positions are computed against the unfiltered tree.
  // Defaults to canEdit so callers that don't filter keep their behaviour.
  canReorder?: boolean
  // Row kept on screen only to give a matching child requirement its parent — it is
  // not itself a match, so it is dimmed.
  contextOnly?: boolean
  // Active search text: highlighting is applied to the DOM from the panel, but the
  // body still needs it to decide whether to open itself (see RequirementDescription).
  searchText: string
  // Read from the filter's comment index instead of scanning the comment list here.
  // Two scans per row on every store dispatch cost ~9 ms with a few hundred rows
  // mounted; the index the filter already builds answers the same question in O(1).
  commentCounts: CommentCounts
  // Every sortable id in the table, so a resize here re-measures all of them. See
  // the note on useSortable below. Must be referentially stable — it is a prop on a
  // memoised component; the panel's `visible` memo owns it.
  measureIds?: AppId[]
}

type FullRowProps = URSRequirementRowProps & { mask: ReactNode }

// Memoised because the panel re-renders for reasons that concern no individual row
// — a facet toggle, a tab switch, a comment landing on some other requirement — and
// without this every row on the tab re-runs useSortable and rebuilds its shell. Every
// prop the panel passes is now referentially stable across such a render: the
// callbacks are useCallback'd against stable deps, `group` and the id arrays come out
// of the panel's `visible` memo, and `commentCounts` is an entry in the filter index.
//
// This does not break dragging. useSortable subscribes to DndContext, and a context
// change re-renders its consumer regardless of memo.
export const URSRequirementRow = memo((props: FullRowProps) => {
  return <SortableRequirementRow {...props} />
})

const SortableRequirementRow = (props: FullRowProps) => {
  const canReorder = props.canReorder ?? props.canEdit
  const sortable = useSortable({
    id: props.requirement.id,
    // Both halves, spelled out. dnd-kit reads the boolean shorthand as
    // `{draggable: value, droppable: false}` for backwards compatibility, so
    // `disabled: !canReorder` left a deleted requirement as a live drop target —
    // you could not drag it, but you could drop another requirement onto it.
    // The row stays registered either way, so its rect is still measured and the
    // list reflows around it normally during a drag.
    disabled: { draggable: !canReorder, droppable: !canReorder },
    // Droppable rects are measured once, at drag start (MeasuringStrategy.
    // WhileDragging). A row below the fold is measured at its one-line "Loading
    // data…" height, and then autoscroll brings it into view, its
    // IntersectionObserver fires, the real body mounts and the row grows — so every
    // rect below it is stale and the drop lands in the wrong place.
    //
    // dnd-kit re-measures on resize, but by default only for
    // `itemsAfterCurrentSortable`: the ids of *this row's own* SortableContext. The
    // panel nests one context per parent requirement and per group, so a row growing
    // inside one group never re-measures the following groups, even though they all
    // moved. Overriding updateMeasurementsFor with every id in the table fixes that;
    // this key is the documented override point (it spreads after dnd-kit's own).
    //
    // timeout 0 rather than the default 25 ms only shortens the window in which
    // collisions still run against the stale rects — the debounce is per row, so it
    // delays a measurement rather than starving it.
    // Spread rather than `updateMeasurementsFor: props.measureIds`: passing the key
    // as undefined would override dnd-kit's own default with "measure only me",
    // which is worse than leaving it alone.
    resizeObserverConfig: {
      timeout: 0,
      ...(props.measureIds ? { updateMeasurementsFor: props.measureIds } : {}),
    },
  })
  return <RequirementRowBody
    {...props}
    setRowNode={sortable.setNodeRef}
    transform={sortable.transform}
    transition={sortable.transition}
    isDragging={sortable.isDragging}
    listeners={sortable.listeners}
    attributes={sortable.attributes}
    isDraggable={canReorder}
  />
}

// TODO: dead code since child requirements became sortable — PlainRequirementRow
// is no longer referenced. Remove once we're sure no non-draggable row variant is needed.
const PlainRequirementRow = (props: FullRowProps) => {
  return <RequirementRowBody
    {...props}
    setRowNode={null}
    transform={null}
    transition={undefined}
    isDragging={false}
    listeners={undefined}
    attributes={undefined}
    isDraggable={false}
  />
}

type RowBodyProps = FullRowProps & {
  setRowNode: ((node: HTMLElement | null) => void) | null
  transform: { x: number; y: number; scaleX: number; scaleY: number } | null
  transition: string | undefined
  isDragging: boolean
  listeners: Record<string, Function> | undefined
  attributes: Record<string, any> | undefined
  isDraggable: boolean
}

const RequirementRowBody = (props: RowBodyProps) => {
  const { requirement, tableRef, mask, setRowNode, transform, transition, isDragging, listeners, attributes, isDraggable, contextOnly } = props
  const rowRef = useRef<null | HTMLTableRowElement>(null)
  const observer = useRef<IntersectionObserver | null>(null)
  const previousStatus = useRef(requirement.status)

  const [shown, setShown] = useState(false)
  const [recentlyRestored, setRecentlyRestored] = useState(false)

  useEffect(() => {
    if (tableRef.current && rowRef.current) {
      observer.current = new IntersectionObserver((entries) => {
        if (entries.length === 1) {
          const element = entries[0]
          if (element.isIntersecting) {
            if (observer.current) {
              observer.current.unobserve(element.target)
              observer.current.disconnect()
            }
            setShown(true)
          }
        }
      }, {
        root: tableRef.current,
        threshold: .2,
      })
      observer.current.observe(rowRef.current)
    }
  }, [])

  useEffect(() => {
    const wasDeleted = previousStatus.current === 'DELETED'
    const isDeletedNow = requirement.status === 'DELETED'

    if (wasDeleted && !isDeletedNow) {
      setRecentlyRestored(true)
      const timer = window.setTimeout(() => setRecentlyRestored(false), 2500)
      previousStatus.current = requirement.status
      return () => window.clearTimeout(timer)
    }

    previousStatus.current = requirement.status
  }, [requirement.status])

  // Must be stable. React detaches and reattaches a callback ref whenever its
  // identity changes, and dnd-kit's setNodeRef reacts to that by calling
  // resizeObserver.unobserve(old) — which resets its `resizeObserverConnected` flag —
  // then observe(new). The next callback is then swallowed as the "initial"
  // observation every ResizeObserver delivers on observe(). Rows re-render constantly
  // during a drag (dnd-kit's InternalContext changes on every `over`), so an inline
  // ref meant the flag was reset continuously and genuine resizes never reached
  // measureDroppableContainers: a row that swapped its mask for real content grew
  // without dnd-kit ever re-measuring, and the drop landed in the wrong place.
  const setRefs = useCallback((node: HTMLTableRowElement | null) => {
    rowRef.current = node
    setRowNode?.(node)
  }, [setRowNode])

  const content = shown ? <Memoized {...props} recentlyRestored={recentlyRestored}></Memoized> : mask

  return <TableRow
    ref={setRefs}
    id={requirement.guid}
    style={{
      transform: CSS.Transform.toString(transform),
      transition,
    }}
    sx={{
      // Three independent row states, in precedence order: being dragged, kept on
      // screen only as context for a matching child, or ordinary.
      opacity: isDragging ? 0.4 : contextOnly ? 0.45 : 1,
      transition: 'background-color 0.35s ease',
      ...(recentlyRestored ? {
        backgroundColor: 'rgba(76, 175, 80, 0.04)',
        boxShadow: 'inset 3px 0 0 rgba(46, 125, 50, 0.45)',
        '& > td': {
          backgroundColor: 'rgba(76, 175, 80, 0.04)'
        },
      } : {}),
      '&:target': {
        boxShadow: 'inset 0 2px 4px 0 rgb(0 0 0 / 0.05), 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
        backgroundColor: '#f1f5f9',
      },
    }}
    {...(attributes ?? {})}
  >
    <TableCell
      sx={{
        width: 32,
        maxWidth: 32,
        padding: '0 4px',
        cursor: isDraggable ? 'grab' : 'default',
        touchAction: 'none',
      }}
      {...(isDraggable && listeners ? listeners : {})}
    >
      {isDraggable && (
        <DragIndicatorIcon fontSize="small" sx={{ color: 'action.disabled', display: 'block' }} />
      )}
    </TableCell>
    {content}
  </TableRow>
}

const Memoized = (props: URSRequirementRowProps & { recentlyRestored?: boolean }) => {
  const { requirement } = props

  // The counts arrive as props now, so they have to be listed here: the badges used
  // to refresh through their own redux subscription, which re-rendered this subtree
  // even when the memo held. Primitives, not the CommentCounts object — the index is
  // rebuilt whenever comments *or* blobs change, so its entries are never stable.
  const row = useMemo(() => {
    return <URSRequirementRowInternal {...props}></URSRequirementRowInternal>
  }, [requirement.modifyDate, requirement.status, requirement.deleteReason, requirement.deletionReason,
      props.recentlyRestored, props.commentCounts.total, props.commentCounts.unresolved, props.searchText])

  return <>{row}</>
}

const URSRequirementRowInternal = (props: URSRequirementRowProps & { recentlyRestored?: boolean }) => {
  const dispatch = useAppDispatch()
  const { id, onAddChildRequirmentClick, onEditClick, onCopyClick, onDeleteClick, canEdit, canAddChildRequirement, canDelete, showSupplierComment, onCommentsClick, onAttachementsClick, template, requirement, group, commentCounts, searchText, recentlyRestored = false } = props
  const isDeleted = requirement.status === 'DELETED'
  const requirementNumber = requirement.numeration ?? requirement.code ?? requirement.id
  // Keeps the `|| ""` fallback: this also seeds the edit-reason dialog's input.
  const deleteReason = requirement.deletionReason?.trim() || requirement.deleteReason?.trim() || ""
  const { total: commentsCount, unresolved: unresolvedCommentsCount } = commentCounts
  const blobs = useAppSelector(state => selectBlobsByResourceId(state, requirement.guid))
  const attachementsCount = blobs?.blobs?.length ?? 0
  const isNew: boolean = requirement.id === undefined || requirement.id.startsWith("new")

  const [reasonDialogOpen, setReasonDialogOpen] = useState(false)
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false)
  const [reasonInput, setReasonInput] = useState(deleteReason)
  const [savingReason, setSavingReason] = useState(false)
  const [restoring, setRestoring] = useState(false)

  useEffect(() => {
    setReasonInput(deleteReason)
  }, [deleteReason, requirement.id])

  const handleReasonSave = async () => {
    const trimmedReason = reasonInput.trim().slice(0, DELETE_REASON_MAX_LENGTH)
    setSavingReason(true)
    try {
      await dispatch(updateDeletedUrsRequirementReason({ id: requirement.id, reason: trimmedReason })).unwrap()
      dispatch(showSuccess("saved"))
      setReasonDialogOpen(false)
    } catch {
      dispatch(showError("error"))
    } finally {
      setSavingReason(false)
    }
  }

  const handleRestore = async () => {
    setRestoring(true)
    try {
      await dispatch(restoreUrsRequirement(requirement.id)).unwrap()
      dispatch(showSuccess("restored"))
      setRestoreDialogOpen(false)
    } catch {
      dispatch(showError("error"))
    } finally {
      setRestoring(false)
    }
  }

  return <>
    <TableCell
      sx={{
        paddingRight: requirement.parentRequirementId ? '20px' : '2px',
        textAlign: requirement.parentRequirementId ? 'right' : 'left',
        backgroundColor: requirement.parentRequirementId ? '#fafafa' : 'inherit',
        mminWidth: 110, width: 110, maxWidth: 110
      }}
    >
      <DocumentNumberBox value={requirement.numeration} />
    </TableCell>
    <TableCell sx={{
      ['& p img']: {
        maxWidth: '600px',
      },
    }}>
      <SmallStatusBadge label={requirement.status} />
      {/* data-req-content is what the panel's highlighter selects on: it scopes the
          text walk to requirement bodies, keeping numbering, status chips and the
          supplier comment column out of the match set. */}
      <div data-req-content style={isDeleted ? { textDecoration: 'line-through', color: 'red' } : undefined}>
        <RequirementDescription description={requirement.name} searchText={searchText} />
      </div>
      {recentlyRestored && (
        <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block', fontWeight: 600 }}>
          <Localized id="restored"><span>Przywrócono</span></Localized>
        </Typography>
      )}
      {isDeleted && deleteReason && (
        <Typography variant="body2" color="error" sx={{ mt: 0.5, fontStyle: 'italic', textDecoration: 'none' }}>
          <Localized id="delete-requirement-reason-display" vars={{ reason: deleteReason }}>
            <span>Powod usuniecia: {deleteReason}</span>
          </Localized>
        </Typography>
      )}
    </TableCell>
    <TableCell>{<ReqCriticalityLabel criticality={requirement.criticality ?? ""} />}</TableCell>
    <If condition={showSupplierComment}>
      <TableCell sx={{ textAlign: "center" }}>
        <NoteLabel note={requirement.note} />
      </TableCell>
      <TableCell>{requirement.comment}</TableCell>
    </If>
    <TableCell sx={{ textAlign: isDeleted ? 'right' : 'left' }}>
      <ButtonGroup variant="outlined" color="secondary" size="small">
        {/* Hiding while a reorder is pending is done in CSS, from a class on the table
            container — see REORDER_HIDE / REORDER_HIDE_SLOT and RequirementsPanel.
            These used to be gated on a `reorderMode` prop, which meant entering or
            leaving reorder mode re-rendered every row body and mounted/unmounted its
            buttons: ~1800 DOM nodes and ~776 ms at 200 rows, on the first drop and
            again on Cancel. ButtonGroup's `grouped` class never reaches these buttons
            (its children are <If> fragments, which drop className), so hiding one does
            not disturb the group's first/last-child borders. */}
        <If condition={requirement.parentRequirementId == null && canAddChildRequirement && !isDeleted} otherwise={
          (<IconButton size="small" style={{ visibility: 'hidden' }}>
            <AddCircleIcon />
          </IconButton>)
        }>
          <Tooltip title={<Localized id="">Dodaj podwymaganie</Localized>}>
            {/* Keeps its slot rather than collapsing, matching the hidden placeholder
                the `otherwise` branch renders. */}
            <IconButton className={REORDER_HIDE_SLOT} size="small" onClick={() => onAddChildRequirmentClick(requirement)}><AddCircleIcon /></IconButton>
          </Tooltip>
        </If>
        <If condition={!props.readOnly && (template || canEdit) && !isDeleted}>
          <Tooltip title={<Localized id="edit">Edytuj</Localized>}>
            <IconButton className={REORDER_HIDE} size="small" onClick={() => onEditClick(id, group, requirement, props.rt)}><EditIcon /></IconButton>
          </Tooltip>
          <Tooltip title={<Localized id="copy">Kopiuj</Localized>}>
            <IconButton className={REORDER_HIDE} size="small" onClick={() => onCopyClick(id, group, requirement, props.rt)}><ContentCopyIcon /></IconButton>
          </Tooltip>
        </If>
        <If condition={!props.readOnly && (template || canDelete) && !isDeleted}>
          <Tooltip title={<Localized id="delete">Usuń</Localized>}>
            <IconButton className={REORDER_HIDE} size="small" onClick={() => onDeleteClick(requirement)}><DeleteForeverIcon /></IconButton>
          </Tooltip>
        </If>
        <If condition={!props.readOnly && (template || canEdit || canDelete) && isDeleted}>
          <Tooltip title={<Localized id="delete-requirement-reason-edit-tooltip">Edytuj powód usunięcia</Localized>}>
            <IconButton className={REORDER_HIDE} size="small" onClick={() => setReasonDialogOpen(true)}>
              <EditNoteIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={<Localized id="restore-requirement-tooltip">Przywróć wymaganie</Localized>}>
            {/* The span is what carries the tooltip while the button is disabled, so it
                is the element that has to go. */}
            <span className={REORDER_HIDE}>
              <IconButton size="small" onClick={() => setRestoreDialogOpen(true)} disabled={restoring}>
                <UndoIcon />
              </IconButton>
            </span>
          </Tooltip>
        </If>
        <If condition={!template}>
          <Badge color="info" badgeContent={attachementsCount} invisible={attachementsCount === 0}>
            <Tooltip title={<Localized id="attachements">Załączniki</Localized>}>
              <IconButton disabled={isNew} size="small" onClick={() => onAttachementsClick(requirement)}>
                <AttachmentIcon />
              </IconButton>
            </Tooltip>
          </Badge>
          <Badge color="info" badgeContent={commentsCount} invisible={commentsCount === 0}>
            <Badge color="error" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} badgeContent={unresolvedCommentsCount} invisible={unresolvedCommentsCount === 0}>
              <Tooltip title={<Localized id="comments">Komentarze</Localized>}>
                <IconButton disabled={isNew} size="small" onClick={() => onCommentsClick(requirement)}><CommentIcon /></IconButton>
              </Tooltip>
            </Badge>
          </Badge>
        </If>
      </ButtonGroup>
    </TableCell>

    <Dialog open={reasonDialogOpen} onClose={() => setReasonDialogOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle>
        <Localized id="delete-requirement-reason-edit-title">
          <span>Edit deletion reason</span>
        </Localized>
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          maxRows={8}
          margin="dense"
          value={reasonInput}
          onChange={(event) => setReasonInput(event.target.value.slice(0, DELETE_REASON_MAX_LENGTH))}
          inputProps={{ maxLength: DELETE_REASON_MAX_LENGTH }}
          label={<Localized id="delete-requirement-reason"><span>Deletion reason</span></Localized>}
          helperText={`${reasonInput.length}/${DELETE_REASON_MAX_LENGTH}`}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setReasonDialogOpen(false)} disabled={savingReason}>
          <Localized id="cancel"><span>Cancel</span></Localized>
        </Button>
        <Button variant="contained" onClick={() => void handleReasonSave()} disabled={savingReason}>
          <Localized id="save"><span>Save</span></Localized>
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog open={restoreDialogOpen} onClose={() => setRestoreDialogOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle>
        <Localized id="restore-requirement-confirm-title">
          <span>Restore requirement</span>
        </Localized>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          <Typography variant="body1">
            <Localized id="restore-requirement-confirm-question" vars={{ requirementNumber }}>
              <span>Are you sure you want to restore requirement {requirementNumber}?</span>
            </Localized>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <Localized id="restore-requirement-confirm-renumbering">
              <span>The requirement number may differ because it will be recalculated.</span>
            </Localized>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <Localized id="restore-requirement-confirm-placement">
              <span>The system will try to place it in its previous position.</span>
            </Localized>
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setRestoreDialogOpen(false)} disabled={restoring}>
          <Localized id="cancel"><span>Cancel</span></Localized>
        </Button>
        <Button variant="contained" onClick={() => void handleRestore()} disabled={restoring}>
          <Localized id="restore"><span>Restore</span></Localized>
        </Button>
      </DialogActions>
    </Dialog>
  </>
}
